import React from 'react';
import { AuthProvider } from '../utils/AuthContext';
import { UserProvider } from '../utils/UserContext';
import { ScreenViewProvider } from '../utils/ScreenViewContext';
import { DemoProvider } from '../utils/DemoContext';
import { DemoAuthProvider } from '../utils/DemoAuthProvider';
import { DemoUserProvider } from '../utils/DemoUserProvider';
import { FeatureFlagProvider } from './FeatureFlagProvider';
import { BrandingProvider } from './BrandingProvider';
import ProtectedRoute from '../components/ProtectedRoute';

type ProvidersProps = { children: React.ReactNode };

/**
 * Provider stack for the public, unauthenticated-but-account-aware routes
 * (login, role select, driver/partner signup). Order matches the original
 * per-route nesting exactly: Auth > User > ScreenView.
 */
export const AppProviders: React.FC<ProvidersProps> = ({ children }) => (
  <AuthProvider>
    <UserProvider>
      <ScreenViewProvider>{children}</ScreenViewProvider>
    </UserProvider>
  </AuthProvider>
);

/**
 * Provider stack for authenticated app routes (dashboard, signup). Adds the
 * auth gate plus feature-flag and branding context inside the Auth/User
 * providers, with ScreenView innermost — identical order to the original
 * per-route nesting: Auth > User > ProtectedRoute > FeatureFlag > Branding >
 * ScreenView.
 */
export const ProtectedProviders: React.FC<ProvidersProps> = ({ children }) => (
  <AuthProvider>
    <UserProvider>
      <ProtectedRoute>
        <FeatureFlagProvider>
          <BrandingProvider>
            <ScreenViewProvider>{children}</ScreenViewProvider>
          </BrandingProvider>
        </FeatureFlagProvider>
      </ProtectedRoute>
    </UserProvider>
  </AuthProvider>
);

/**
 * Provider stack for the demo experience. Order matches the original per-route
 * nesting exactly: Demo > DemoAuth > DemoUser > ScreenView.
 */
export const DemoProviders: React.FC<ProvidersProps> = ({ children }) => (
  <DemoProvider>
    <DemoAuthProvider>
      <DemoUserProvider>
        <ScreenViewProvider>{children}</ScreenViewProvider>
      </DemoUserProvider>
    </DemoAuthProvider>
  </DemoProvider>
);
